<template>
  <div v-show="show" class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">メンバー管理</h2>
          <button type="button" class="modal-close-btn" aria-label="閉じる" @click="$emit('close')">×</button>
        </div>

        <div class="modal-body">
          <div class="members-list-section">
            <h3 class="section-subtitle">メンバー一覧</h3>
            <div class="members-list">
              <div
                v-for="member in members"
                :key="member.MEMBER_CODE"
                class="member-row"
              >
                <div
                  class="member-item"
                  :class="{ 'is-me': member.MEMBER_CODE === myMemberCode }"
                >
                  <div class="member-info">
                    <span class="member-name">
                      {{ member.MEMBER_NAME }}
                      <span v-if="member.MEMBER_CODE === myMemberCode" class="me-badge">あなた</span>
                      <span v-if="!member.APP_USER_CODE" class="temp-badge">仮</span>
                    </span>
                    <span class="member-role" v-if="isOwnerMember(member)">オーナー</span>
                    <span class="member-role member-role-regular" v-else>メンバー</span>
                  </div>
                  <div class="member-actions-inner">
                    <button
                      v-if="canManageMembers"
                      type="button"
                      class="btn-edit-member"
                      @click="toggleEditMember(member.MEMBER_CODE)"
                    >
                      編集
                    </button>
                    <button
                      v-if="member.MEMBER_CODE === myMemberCode"
                      type="button"
                      class="btn-leave-board"
                      :disabled="isOwner"
                      @click="confirmLeave"
                      :title="isOwner ? 'オーナーは退出できません' : 'このボードから退出する'"
                    >
                      退出
                    </button>
                    <button
                      v-else-if="canManageMembers"
                      type="button"
                      class="btn-remove-member"
                      title="このメンバーを削除する"
                      @click="$emit('remove-member', member.MEMBER_CODE)"
                    >
                      削除
                    </button>
                  </div>
                </div>

                <div
                  v-if="canManageMembers && editingMemberCode === member.MEMBER_CODE"
                  class="member-edit-form"
                >
                  <h4>"{{ member.MEMBER_NAME }}" を編集</h4>
                  <div>
                    <label :for="'edit-member-name-' + member.MEMBER_CODE" class="form-label">表示名</label>
                    <input
                      v-model="editMemberForm.memberName"
                      type="text"
                      :id="'edit-member-name-' + member.MEMBER_CODE"
                      class="form-input compact-input"
                      maxlength="40"
                    />
                  </div>
                  <div>
                    <label :for="'edit-member-email-' + member.MEMBER_CODE" class="form-label">メールアドレス</label>
                    <input
                      v-model="editMemberForm.inviteEmail"
                      type="email"
                      :id="'edit-member-email-' + member.MEMBER_CODE"
                      class="form-input compact-input"
                      placeholder="例: user@example.com"
                    />
                  </div>
                  <div class="inline-actions">
                    <button type="button" class="btn btn-secondary compact-btn" @click="editingMemberCode = null">キャンセル</button>
                    <button type="button" class="btn btn-primary compact-btn" @click="submitUpdateMember(member.MEMBER_CODE)">保存</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="canManageMembers" class="invite-section">
            <h3 class="section-subtitle">メンバーを追加</h3>
            <form @submit.prevent="submitInvite" class="modal-form">
              <div class="form-group" v-if="friendCandidates && friendCandidates.length > 0">
                <label class="form-label" for="select-friend">友達リストから追加</label>
                <select id="select-friend" class="form-input" @change="onFriendSelect">
                  <option value="">友達を選択...</option>
                  <option v-for="friend in friendCandidates" :key="friend.APP_USER_CODE" :value="friend.EMAIL">
                    {{ friend.USER_NAME }} ({{ friend.EMAIL }})
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label" for="invite-member-name">追加するメンバーの表示名 *</label>
                <input
                  v-model="inviteForm.memberName"
                  type="text"
                  id="invite-member-name"
                  class="form-input"
                  placeholder="例: 太郎"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label" for="invite-email">メールアドレス (任意)</label>
                <input
                  v-model="inviteForm.inviteEmail"
                  type="email"
                  id="invite-email"
                  class="form-input"
                  placeholder="例: user@example.com"
                />
              </div>
              <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading">メンバーを追加する</button>
            </form>
          </div>

          <div v-if="canManageMembers" class="share-link-section">
            <h3 class="section-subtitle no-border">招待リンク</h3>
            <div v-if="issuedInviteUrl" class="one-time-link-panel">
              <p class="share-notice">
                このリンクは今だけ表示されています。必要な相手へ共有してから閉じてください。
              </p>
              <div class="share-row">
              <input
                type="text"
                readonly
                class="form-input share-input"
                :value="issuedInviteUrl"
                aria-label="今回発行した招待リンク"
              />
              <button
                type="button"
                class="btn btn-secondary copy-button"
                @click="copyShareUrl"
              >
                {{ copyStatus }}
              </button>
              </div>
            </div>
            <div v-else-if="inviteMetadata?.isActive" class="share-metadata-panel">
              <p class="share-status">招待リンクは発行済みです。</p>
              <p class="share-notice">
                安全のため既存リンクは再表示できません。必要な場合は再発行してください。
              </p>
              <p v-if="inviteMetadata.expiresAt" class="share-expiry">
                有効期限: {{ formatExpiry(inviteMetadata.expiresAt) }}
              </p>
              <div class="share-actions">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="isLoading"
                  @click="$emit('rotate-share-token')"
                >
                  招待リンクを再発行
                </button>
                <button
                  type="button"
                  class="btn btn-danger"
                  :disabled="isLoading"
                  @click="$emit('revoke-share-token', inviteMetadata.tokenId)"
                >
                  招待リンクを無効化
                </button>
              </div>
            </div>
            <div v-else>
              <p class="share-notice">招待リンクは発行されていません。</p>
              <button
                type="button"
                class="btn btn-secondary btn-block"
                :disabled="isLoading"
                @click="$emit('generate-share-token')"
              >
                招待リンクを生成する
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";

export default {
  name: "MemberManageModal",
  props: {
    show: { type: Boolean, required: true },
    members: { type: Array, required: true },
    isOwner: { type: Boolean, required: true },
    myMemberCode: { type: String, default: null },
    ownerUserCode: { type: String, required: true },
    inviteMetadata: { type: Object, default: null },
    issuedInviteUrl: { type: String, default: "" },
    isLoading: { type: Boolean, default: false },
  },
  emits: [
    "close",
    "invite",
    "remove-member",
    "leave-board",
    "update-member",
    "generate-share-token",
    "rotate-share-token",
    "revoke-share-token",
  ],
  setup(props, { emit }) {
    const shioriMemberStore = useShioriMemberStore();
    const copyStatus = ref("コピー");
    const canManageMembers = computed(() => props.isOwner);

    const inviteForm = ref({
      memberName: "",
      inviteEmail: "",
    });

    const editingMemberCode = ref(null);
    const editMemberForm = ref({
      memberName: "",
      inviteEmail: "",
    });

    const fetchFriendCandidates = async () => {
      if (!props.isOwner) return;
      try {
        await shioriMemberStore.fetchFriendCandidates();
      } catch (err) {
        console.error("Failed to fetch friends:", err);
      }
    };

    onMounted(() => {
      if (props.isOwner) fetchFriendCandidates();
    });

    watch(
      () => props.show,
      (newShow) => {
        if (newShow && props.isOwner) fetchFriendCandidates();
      }
    );

    const onFriendSelect = (e) => {
      const email = e.target.value;
      if (!email) return;
      const friend = shioriMemberStore.friendCandidates.find((f) => f.EMAIL === email);
      if (friend) {
        inviteForm.value.memberName = friend.USER_NAME;
        inviteForm.value.inviteEmail = friend.EMAIL;
      }
      e.target.value = "";
    };

    const copyShareUrl = async () => {
      try {
        await navigator.clipboard.writeText(props.issuedInviteUrl);
        copyStatus.value = "コピーしました";
        setTimeout(() => {
          copyStatus.value = "コピー";
        }, 2000);
      } catch {
        console.error("Failed to copy invite link");
        alert("コピーに失敗しました");
      }
    };

    const formatExpiry = (value) => {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "不明"
        : date.toLocaleString("ja-JP");
    };

    const toggleEditMember = (memberCode) => {
      if (!props.isOwner) return;
      if (editingMemberCode.value === memberCode) {
        editingMemberCode.value = null;
        return;
      }
      const member = props.members.find((m) => m.MEMBER_CODE === memberCode);
      editMemberForm.value = {
        memberName: member?.MEMBER_NAME || "",
        inviteEmail: member?.INVITE_EMAIL || "",
      };
      editingMemberCode.value = memberCode;
    };

    const submitUpdateMember = (memberCode) => {
      if (!props.isOwner) return;
      emit("update-member", {
        memberCode,
        memberData: {
          memberName: editMemberForm.value.memberName ? editMemberForm.value.memberName.trim() : null,
          appUserCode: null,
          inviteEmail: editMemberForm.value.inviteEmail ? editMemberForm.value.inviteEmail.trim() : null,
        },
      });
      editingMemberCode.value = null;
    };

    const isOwnerMember = (member) => member.APP_USER_CODE === props.ownerUserCode;

    const confirmLeave = () => {
      if (props.isOwner) {
        alert("オーナーは退出できません。ボードを削除するか、オーナー権限を他のメンバーに譲渡してください。");
        return;
      }
      if (confirm("このボードから退出しますか？退出すると、このボードの閲覧や編集ができなくなります。")) {
        emit("leave-board", props.myMemberCode);
      }
    };

    const submitInvite = () => {
      if (!props.isOwner) return;
      emit("invite", {
        memberName: inviteForm.value.memberName.trim(),
        appUserCode: null,
        inviteEmail: inviteForm.value.inviteEmail ? inviteForm.value.inviteEmail.trim() : null,
      });
      inviteForm.value = {
        memberName: "",
        inviteEmail: "",
      };
    };

    return {
      inviteForm,
      editingMemberCode,
      editMemberForm,
      toggleEditMember,
      submitUpdateMember,
      isOwnerMember,
      confirmLeave,
      submitInvite,
      friendCandidates: computed(() => shioriMemberStore.friendCandidates),
      onFriendSelect,
      canManageMembers,
      copyShareUrl,
      copyStatus,
      formatExpiry,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  background-image: radial-gradient(rgba(46, 125, 50, 0.05) 1px, transparent 0);
  background-size: 16px 16px;
}

.modal-container {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.modal-header {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--text-main);
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 1.75rem;
  color: var(--text-muted);
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  color: var(--text-main);
}

.modal-body {
  padding: 1.75rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.section-subtitle {
  font-size: 1rem;
  font-weight: 800;
  color: var(--text-main);
  margin: 0 0 1rem 0;
  font-family: 'Space Grotesk', sans-serif;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  padding-bottom: 0.5rem;
}

.section-subtitle.no-border {
  border: none;
  padding: 0;
}

.members-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.member-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.member-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
}

.member-item.is-me {
  background: rgba(46, 125, 50, 0.05);
  border-color: rgba(46, 125, 50, 0.15);
}

.member-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.member-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-main);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.me-badge,
.temp-badge {
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-weight: 600;
}

.me-badge {
  background: var(--primary-color);
  color: white;
}

.temp-badge {
  background: #cbd5e1;
  color: #475569;
}

.member-role {
  font-size: 0.75rem;
  color: var(--accent-gold);
  font-weight: 700;
}

.member-role-regular {
  color: var(--text-muted);
}

.member-actions-inner,
.inline-actions,
.share-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-edit-member,
.btn-leave-board,
.btn-remove-member {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit-member {
  color: var(--text-main);
  background: white;
  border: 1px solid #cbd5e1;
}

.btn-leave-board,
.btn-remove-member {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.btn-leave-board:hover:not(:disabled),
.btn-remove-member:hover {
  background: rgba(239, 68, 68, 0.15);
}

.btn-leave-board:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.member-edit-form {
  background: #ffffff;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.member-edit-form h4 {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 800;
  color: var(--text-main);
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  display: block;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
}

.compact-input {
  padding: 0.4rem;
  font-size: 0.8125rem;
}

.form-input:focus {
  border-color: var(--primary-color);
}

.btn-block {
  width: 100%;
  padding: 0.75rem;
}

.compact-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
}

.share-link-section {
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  padding-top: 1.5rem;
}

.share-row {
  margin-top: 0.5rem;
}

.share-notice,
.share-expiry,
.share-status {
  margin: 0.5rem 0;
  color: var(--text-muted);
  font-size: 0.8125rem;
  line-height: 1.6;
}

.share-status {
  color: var(--text-main);
  font-weight: 700;
}

.share-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.btn-danger {
  border: 1px solid #dc2626;
  background: #fff;
  color: #b91c1c;
}

.share-input {
  font-size: 0.8125rem;
  background-color: #f8fafc;
}

.copy-button {
  white-space: nowrap;
  font-weight: 700;
}

.share-empty {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .member-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1rem;
  }
  .member-actions-inner {
    justify-content: flex-end;
  }
}
</style>
